import { useContext, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { MarketingMessage } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { RunSelectorPanel } from './RunSelectorPanel';
import { MessageGenerationStateContext } from '../MessageGenSession/Context';

interface MessageGeneratorPanelProps {
  mode: 'selector' | 'messages';
  onSelectRun?: (segmentationSessionId: string, segmentIds: string[], runName: string) => void;
  onSelectSegment?: (
    segmentationSessionId: string,
    segmentId: string,
    segmentName: string,
    runName: string
  ) => void;
  onSelectionChange?: (
    hasSelection: boolean,
    continueHandler: (() => void) | null,
    selectionLabel: string | null
  ) => void;
}

const columnHelper = createColumnHelper<MarketingMessage>();

export function MessageGeneratorPanel({
  mode,
  onSelectRun,
  onSelectSegment,
  onSelectionChange,
}: MessageGeneratorPanelProps): React.JSX.Element {
  const [sorting, setSorting] = useState<SortingState>([]);
  const session = useContext(MessageGenerationStateContext);
  const apiClient = useChatApiClient();

  const messages = useMemo(() => {
    if (!session) return [];
    return session.state.messages;
  }, [session]);

  const saveMutation = useMutation({
    mutationFn: () => apiClient.messageGeneration.saveMessages(session?.sessionId ?? ''),
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Title',
        cell: (info) => <TitleCell>{info.getValue()}</TitleCell>,
        size: 180,
      }),
      columnHelper.accessor('content', {
        header: 'Message',
        cell: (info) => <MessageCell>{info.getValue()}</MessageCell>,
        size: 280,
      }),
      columnHelper.accessor('category', {
        header: 'Category',
        cell: (info) => <CategoryBadge>{info.getValue()}</CategoryBadge>,
        size: 100,
      }),
      // columnHelper.accessor('target_audience', {
      //   header: 'Audience',
      //   cell: (info) => info.getValue(),
      //   size: 100,
      // }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue()}>{info.getValue()}</StatusBadge>,
        size: 90,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: messages,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // If mode is selector, render the RunSelectorPanel (AFTER all hooks)
  if (mode === 'selector') {
    return (
      <RunSelectorPanel
        onSelectRun={(sessionId, segmentIds, runName) => onSelectRun?.(sessionId, segmentIds, runName)}
        onSelectSegment={(sessionId, segmentId, segmentName, runName) =>
          onSelectSegment?.(sessionId, segmentId, segmentName, runName)
        }
        onSelectionChange={onSelectionChange}
      />
    );
  }

  return (
    <Root>
      <PanelHeader>
        <HeaderLeft>
          <HeaderTitle>Generated Messages</HeaderTitle>
          <MessageCount>{messages.length} messages</MessageCount>
        </HeaderLeft>
        {messages.length > 0 && (
          <SaveButton
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !session?.sessionId}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Messages'}
          </SaveButton>
        )}
      </PanelHeader>

      <TableContainer>
        <StyledTable>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeaderCell
                    key={header.id}
                    style={{ width: header.getSize() }}
                    onClick={header.column.getToggleSortingHandler()}
                    sortable={header.column.getCanSort()}
                  >
                    <HeaderContent>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <SortIcon>
                          {header.column.getIsSorted() === 'asc' && <ChevronUp size={16} />}
                          {header.column.getIsSorted() === 'desc' && <ChevronDown size={16} />}
                        </SortIcon>
                      )}
                    </HeaderContent>
                  </TableHeaderCell>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} style={{ width: cell.column.getSize() }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>

        {messages.length === 0 && (
          <EmptyState>
            <EmptyStateText>No messages generated yet.</EmptyStateText>
            <EmptyStateSubtext>Messages will appear here as they are generated.</EmptyStateSubtext>
          </EmptyState>
        )}
      </TableContainer>
    </Root>
  );
}

// Styled Components
const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const HeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.disabled ? '#a0a0a0' : props.theme.palette.sentiment.primary.main)};
  border: none;
  border-radius: 6px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(props) => props.theme.palette.sentiment.primary.dark};
  }
`;

const MessageCount = styled.span`
  font-size: 13px;
  color: #666;
  padding: 4px 10px;
  background: #f5f5f5;
  border-radius: 10px;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const StyledTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 800px;
`;

const TableHeader = styled.div`
  position: sticky;
  top: 0;
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
  z-index: 1;
`;

const TableBody = styled.div`
  flex: 1;
`;

const TableRow = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 1px solid #e8e8e8;
  transition: background 0.15s ease;

  &:hover {
    background: #f9f9f9;
  }
`;

const TableHeaderCell = styled.div<{ sortable?: boolean }>`
  padding: 12px 14px;
  font-weight: 600;
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: ${(props) => (props.sortable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    background: ${(props) => (props.sortable ? '#f5f5f5' : 'transparent')};
  }
`;

const TableCell = styled.div`
  padding: 12px 14px;
  font-size: 13px;
  color: #424242;
  display: flex;
  align-items: center;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${(props) => props.theme.palette.sentiment.primary.main};
`;

const TitleCell = styled.div`
  font-weight: 500;
  color: #1a1a1a;
`;

const MessageCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
`;

const CategoryBadge = styled.span`
  padding: 4px 10px;
  background: ${(props) => props.theme.palette.sentiment.primary.lighter};
  color: ${(props) => props.theme.palette.sentiment.primary.main};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: 'draft' | 'approved' | 'published' }>`
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#fef3c7';
      case 'approved':
        return '#d1fae5';
      case 'published':
        return '#dbeafe';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#92400e';
      case 'approved':
        return '#065f46';
      case 'published':
        return '#1e40af';
      default:
        return '#666';
    }
  }};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyStateText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
`;

const EmptyStateSubtext = styled.div`
  font-size: 14px;
  color: #666;
`;
